export function formatFileSize(bytes) {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
        return null;
    }

    const value = Number(bytes);

    if (value < 1000) {
        return `${value} B`;
    }

    if (value < 1000 * 1000) {
        return `${(value / 1000).toFixed(1)} kB`;
    }

    return `${(value / (1000 * 1000)).toFixed(1)} MB`;
}

export function formatDurationSeconds(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
        return null;
    }

    const total = Number(seconds);

    if (total <= 0) {
        return null;
    }

    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainingSeconds = total % 60;

    if (hours > 0) {
        return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
    }

    if (minutes > 0) {
        return remainingSeconds > 0 ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
    }

    return `${remainingSeconds} sec`;
}

export function organizationLabelFromSummary(summary) {
    if (!summary) {
        return null;
    }

    const organization = summary.organization;

    if (organization?.title && organization?.identifier && organization.title !== organization.identifier) {
        return `${organization.title} (${organization.identifier})`;
    }

    if (organization?.title) {
        return organization.title;
    }

    if (organization?.identifier) {
        return organization.identifier;
    }

    return summary.default_organization ?? null;
}

export function normalizePackageStatusValue(status) {
    if (!status) {
        return '';
    }

    const key = String(status).trim().toLowerCase();

    if (key === 'archive' || key === 'archived') {
        return 'archived';
    }

    if (key === 'active') {
        return 'ready';
    }

    if (key === 'ready' || key === 'draft') {
        return key;
    }

    return key;
}

export function resolvePackageStatus(pkg) {
    if (!pkg) {
        return '';
    }

    const candidates = [pkg.display_status, pkg.latest_version?.status, pkg.latestVersion?.status, pkg.status];

    for (const candidate of candidates) {
        if (candidate) {
            return normalizePackageStatusValue(candidate);
        }
    }

    return '';
}

export function presentationFromPackage(pkg) {
    const latestVersion = pkg?.latest_version ?? pkg?.latestVersion ?? null;
    const summary = latestVersion?.manifest_summary ?? {};

    return {
        authoring_tool: pkg?.authoring_tool ?? summary.authoring_tool ?? null,
        organization: pkg?.organization ?? organizationLabelFromSummary(summary),
        mastery_score: pkg?.mastery_score ?? summary.mastery_score ?? null,
        estimated_duration:
            pkg?.estimated_duration ?? formatDurationSeconds(summary.estimated_duration_seconds ?? null),
        // Prefer computing from canonical bytes on the latest version when present so UI
        // reflects actual file bytes instead of a potentially stale formatted string.
        package_size:
            latestVersion?.zip_size_bytes != null
                ? formatFileSize(latestVersion.zip_size_bytes)
                : (pkg?.package_size ?? null),
    };
}

const STANDARD_LABELS = {
    scorm_1_2: 'SCORM 1.2',
    'scorm 1.2': 'SCORM 1.2',
    'SCORM 1.2': 'SCORM 1.2',
    scorm_2004: 'SCORM 2004',
    xapi: 'xAPI',
    aicc: 'AICC',
    cmi5: 'cmi5',
};

export function formatStandardLabel(standard) {
    if (!standard) {
        return null;
    }

    const key = String(standard)
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, '_');

    return STANDARD_LABELS[key] || String(standard);
}

export function formatPackageVersion(pkg) {
    if (!pkg) {
        return null;
    }

    return (
        pkg.version ||
        pkg.version_label ||
        pkg.latest_version?.version_label ||
        pkg.latest_version?.version ||
        pkg.latestVersion?.version_label ||
        pkg.latestVersion?.version ||
        null
    );
}

export function formatScormSessionTime(value) {
    if (value == null || value === '') {
        return null;
    }

    const parts = String(value).split(':');

    if (parts.length >= 3) {
        const hours = Number.parseInt(parts[0], 10) || 0;
        const minutes = Number.parseInt(parts[1], 10) || 0;
        const seconds = Number.parseInt(parts[2], 10) || 0;
        const totalMinutes = hours * 60 + minutes;

        return `${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return String(value);
}

export function formatElearningLessonStatus(status) {
    if (!status) {
        return null;
    }

    const labels = {
        incomplete: 'In Progress',
        browsed: 'In Progress',
        completed: 'Completed',
        passed: 'Passed',
        failed: 'Failed',
    };

    const normalized = String(status).trim().toLowerCase();

    if (labels[normalized]) {
        return labels[normalized];
    }

    return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
}

export function getPackageScoCount(pkg) {
    if (!pkg) {
        return null;
    }

    const count =
        pkg.activities_count ??
        pkg.activitiesCount ??
        pkg.sco_count ??
        pkg.scoCount ??
        pkg.latest_version?.items?.length ??
        pkg.latestVersion?.items?.length ??
        null;

    return count != null ? Number(count) : null;
}

export function hasElearningSubmissionData(submission) {
    const normalized = submission?.lesson_status != null ? submission : normalizeElearningSubmission(submission);

    if (!normalized) {
        return false;
    }

    return (
        normalized.id != null ||
        normalized.created_at != null ||
        normalized.lesson_status != null ||
        (normalized.score_raw != null && normalized.score_raw !== '') ||
        (normalized.session_time != null && normalized.session_time !== '')
    );
}

/**
 * Normalize an eLearning package submission from either the dedicated API
 * endpoint or the ActivitySubmissions Vuex store into a consistent display shape.
 */
export function normalizeElearningSubmission(submission) {
    if (!submission) {
        return null;
    }

    const person = submission.user ?? submission.creator;
    const firstName = person?.first_name ?? '';
    const lastName = person?.last_name ?? '';
    const composedName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const displayName = person?.name || composedName || person?.email || null;

    return {
        id: submission.id,
        attempt: submission.attempt ?? 1,
        created_at: submission.created_at ?? null,
        created_by: submission.created_by ?? person?.id ?? null,
        marked_completed_at: submission.marked_completed_at ?? null,
        is_late: submission.is_late ?? false,
        user: person
            ? {
                  id: person.id ?? submission.created_by ?? null,
                  name: displayName,
              }
            : submission.created_by
              ? { id: submission.created_by, name: null }
              : null,
        lesson_status: submission.lesson_status ?? submission.submittable?.lesson_status ?? null,
        score_raw: submission.score_raw ?? submission.submittable?.score_raw ?? null,
        session_time:
            submission.session_time ??
            submission.submittable?.cmi_data?.['cmi.core.session_time'] ??
            submission.submittable?.session_time ??
            null,
    };
}

/**
 * Whether an e-learning package submission is marked complete in the course.
 * Course completion is tracked on activity_submissions.marked_completed_at, not
 * solely on SCORM lesson_status.
 */
export function isElearningSubmissionCompleted(submissionOrMarkedCompletedAt) {
    const markedCompletedAt =
        submissionOrMarkedCompletedAt &&
        typeof submissionOrMarkedCompletedAt === 'object' &&
        !Array.isArray(submissionOrMarkedCompletedAt)
            ? submissionOrMarkedCompletedAt.marked_completed_at
            : submissionOrMarkedCompletedAt;

    return Boolean(markedCompletedAt);
}

export function formatElearningCompletionStatus(submissionOrMarkedCompletedAt) {
    return isElearningSubmissionCompleted(submissionOrMarkedCompletedAt) ? 'Completed' : 'Not Completed';
}

export function elearningCompletionStatusTagType(submissionOrMarkedCompletedAt) {
    return isElearningSubmissionCompleted(submissionOrMarkedCompletedAt) ? 'is-success' : 'is-light';
}
